import React from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';

const defaultStyles = {
	container: (base) => ({ ...base, width: '100%' }),
	control: (base, state) => ({
		...base,
		minHeight: 38,
		height: 'auto',
		borderColor: state.isFocused ? 'var(--q-brand-500, #187A8C)' : 'var(--q-surface-border, #e2e8f0)',
		borderRadius: 'var(--q-radius-md, 8px)',
		boxShadow: state.isFocused ? 'var(--q-focus-ring, 0 0 0 3px rgba(24, 122, 140, .35))' : 'none',
		backgroundColor: state.isDisabled ? 'var(--q-gray-50, #f8fafc)' : 'var(--q-surface-bg, #ffffff)',
		cursor: state.isDisabled ? 'not-allowed' : 'default',
		transition: 'border-color var(--q-transition-fast, 150ms ease), box-shadow var(--q-transition-fast, 150ms ease)',
		'&:hover': { borderColor: state.isDisabled ? 'var(--q-surface-border, #e2e8f0)' : 'var(--q-brand-500, #187A8C)' },
	}),
	valueContainer: (base) => ({ ...base, minHeight: 36, padding: '2px 10px', flexWrap: 'wrap' }),
	input: (base) => ({ ...base, color: 'var(--q-text-primary, #0f172a)', fontFamily: 'var(--q-font-family, inherit)', fontSize: 14 }),
	singleValue: (base) => ({ ...base, color: 'var(--q-text-primary, #0f172a)', fontSize: 14 }),
	placeholder: (base) => ({ ...base, color: 'var(--q-text-muted, #64748b)', fontSize: 14 }),
	indicatorsContainer: (base) => ({ ...base, height: 36 }),
	clearIndicator: (base) => ({ ...base, color: 'var(--q-text-muted, #64748b)', '&:hover': { color: 'var(--q-danger-solid, #ef4444)' } }),
	dropdownIndicator: (base, state) => ({ ...base, color: state.isFocused ? 'var(--q-brand-500, #187A8C)' : 'var(--q-text-muted, #64748b)', '&:hover': { color: 'var(--q-brand-600, #166e7f)' } }),
	indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--q-surface-border, #e2e8f0)' }),
	menu: (base) => ({
		...base,
		zIndex: 3000,
		marginTop: 4,
		border: '1px solid var(--q-surface-border, #e2e8f0)',
		borderRadius: 'var(--q-radius-md, 8px)',
		backgroundColor: 'var(--q-surface-bg, #ffffff)',
		boxShadow: 'var(--q-surface-shadow, 0 4px 12px rgba(15, 23, 42, .12))',
		overflow: 'hidden',
	}),
	menuPortal: (base) => ({ ...base, zIndex: 3000 }),
	menuList: (base) => ({ ...base, padding: 4 }),
	option: (base, state) => ({
		...base,
		padding: '9px 12px',
		borderRadius: 'var(--q-radius-sm, 6px)',
		fontSize: 14,
		color: state.isSelected ? 'var(--q-color-primary-contrast, #ffffff)' : 'var(--q-text-primary, #0f172a)',
		backgroundColor: state.isSelected ? 'var(--q-color-primary, #187A8C)' : state.isFocused ? 'var(--q-color-primary-subtle, #edfbfc)' : 'transparent',
		cursor: state.isDisabled ? 'not-allowed' : 'pointer',
		'&:active': { backgroundColor: 'var(--q-brand-100, #d1f4f8)' },
	}),
	noOptionsMessage: (base) => ({ ...base, color: 'var(--q-text-muted, #64748b)', fontSize: 13 }),
	loadingMessage: (base) => ({ ...base, color: 'var(--q-text-muted, #64748b)', fontSize: 13 }),
	multiValue: (base) => ({ ...base, borderRadius: 'var(--q-radius-sm, 6px)', backgroundColor: 'var(--q-color-primary-subtle, #edfbfc)' }),
	multiValueLabel: (base) => ({ ...base, color: 'var(--q-brand-700, #135a6a)', fontSize: 12 }),
	multiValueRemove: (base) => ({ ...base, color: 'var(--q-brand-600, #166e7f)', borderRadius: '0 6px 6px 0', '&:hover': { color: 'var(--q-danger-solid, #ef4444)', backgroundColor: 'var(--q-danger-bg, #fee2e2)' } }),
};

const getStyles = (overrides = {}) => Object.fromEntries(
	[...new Set([...Object.keys(defaultStyles), ...Object.keys(overrides)])].map((key) => {
		const defaultStyle = defaultStyles[key];
		const overrideStyle = overrides[key];
		if (typeof overrideStyle === 'function') {
			return [key, (...args) => overrideStyle(defaultStyle ? defaultStyle(...args) : args[0], ...args.slice(1))];
		}
		if (overrideStyle && typeof overrideStyle === 'object') {
			return [key, (...args) => ({ ...(defaultStyle ? defaultStyle(...args) : args[0]), ...overrideStyle })];
		}
		return [key, defaultStyle];
	}),
);

const FormAsyncSelect = ({ styles, classNamePrefix = 'q-async-select', menuPortalTarget = typeof document !== 'undefined' ? document.body : null, menuPosition = 'fixed', ...props }) => (
	<AsyncSelect
		{...props}
		classNamePrefix={classNamePrefix}
		styles={getStyles(styles)}
		menuPortalTarget={menuPortalTarget}
		menuPosition={menuPosition}
	/>
);

FormAsyncSelect.propTypes = {
	styles: PropTypes.object,
	classNamePrefix: PropTypes.string,
	menuPortalTarget: PropTypes.any,
	menuPosition: PropTypes.oneOf(['absolute', 'fixed']),
};

export default FormAsyncSelect;
